import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Eye, Plus } from "lucide-react";
import { toast } from "react-toastify";
import api from "../../api/axios";
import LoadingOverlay from "../../components/LoadingOverlay";
import { formatCurrencyCOP, formatDate } from "../../utils/format";

const statusLabels = {
  draft: "Borrador",
  approved: "Aprobada",
  not_approved: "No aprobada",
  under_review: "Por revisión",
  sent: "Enviada",
  partially_received: "Recepción parcial",
  received: "Recibida",
  cancelled: "Cancelada",
};

const statusStyles = {
  draft: "bg-gray-100 text-gray-700",
  approved: "bg-blue-100 text-blue-700",
  not_approved: "bg-red-100 text-red-700",
  under_review: "bg-yellow-100 text-yellow-700",
  sent: "bg-indigo-100 text-indigo-700",
  partially_received: "bg-yellow-100 text-yellow-700",
  received: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-700",
};

function PurchaseOrders() {
  const [orders, setOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [status, setStatus] = useState("all");
  const [supplierId, setSupplierId] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadSuppliers = async () => {
      try {
        const response = await api.get("/suppliers");
        setSuppliers(response.data || []);
      } catch (error) {
        toast.error("Error al cargar proveedores");
      }
    };

    loadSuppliers();
  }, []);

  useEffect(() => {
    const loadOrders = async () => {
      try {
        setLoading(true);
        const response = await api.get("/purchase-orders", {
          params: { status, supplierId },
        });
        setOrders(response.data || []);
      } catch (error) {
        toast.error("Error al cargar órdenes de compra");
      } finally {
        setLoading(false);
      }
    };

    loadOrders();
  }, [status, supplierId]);

  const getStatus = (orderStatus) => (
    <span
      className={`${statusStyles[orderStatus] || "bg-gray-100 text-gray-700"} px-3 py-1 rounded-full text-xs font-semibold`}
    >
      {statusLabels[orderStatus] || orderStatus}
    </span>
  );

  const getEstimatedTotal = (order) =>
    (order.details || []).reduce(
      (sum, detail) => sum + Number(detail.quantity || 0) * Number(detail.unit_price || 0),
      0,
    );

  return (
    <div className="bg-white p-6 rounded-xl shadow-md relative">
      <LoadingOverlay loading={loading} />
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <h2 className="text-3xl font-semibold text-gray-800">
          Órdenes de compra
        </h2>
        <div className="flex gap-3 items-center">
          <select
            className="border rounded px-3 py-2"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="all">Estados Factura</option>
            {Object.entries(statusLabels).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          <select
            className="border rounded px-3 py-2"
            value={supplierId}
            onChange={(e) => setSupplierId(e.target.value)}
          >
            <option value="all">Todos los proveedores</option>
            {suppliers.map((supplier) => (
              <option key={supplier.id} value={supplier.id}>
                {supplier.name}
              </option>
            ))}
          </select>
          <Link
            to="/purchase-orders/create"
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <Plus size={18} /> Nueva orden
          </Link>
        </div>
      </div>
      <div className="overflow-x-auto rounded-xl shadow">
        <table className="min-w-full bg-white text-center">
          <thead>
            <tr className="bg-gray-100 text-gray-700 text-sm uppercase">
              <th className="px-6 py-3">Número</th>
              <th className="px-6 py-3">Proveedor</th>
              <th className="px-6 py-3">Fecha</th>
              <th className="px-6 py-3">Estado</th>
              <th className="px-6 py-3">Total estimado</th>
              <th className="px-6 py-3">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-6 py-8 text-gray-400 italic">
                  No hay órdenes de compra.
                </td>
              </tr>
            ) : (
              orders.map((order) => (
                <tr key={order.id} className="border-t">
                  <td className="px-6 py-4 font-semibold">
                    {order.order_number}
                  </td>
                  <td className="px-6 py-4">{order.supplier?.name}</td>
                  <td className="px-6 py-4">{formatDate(order.order_date)}</td>
                  <td className="px-6 py-4">
                    {getStatus(order.status)}
                  </td>
                  <td className="px-6 py-4">
                    {formatCurrencyCOP(getEstimatedTotal(order))}
                  </td>
                  <td className="px-6 py-4">
                    <Link
                      to={`/purchase-orders/${order.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex bg-blue-500 hover:bg-blue-600 text-white p-2 rounded-lg"
                      title="Ver orden"
                    >
                      <Eye size={16} />
                    </Link>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default PurchaseOrders;
